import {
  computed,
  type Ref,
} from 'vue';
import {
  BalmSidebarType,
  type BalmSidebarMenuItem,
} from '@/ts/types/component/balm-sidebar';
import {useUserStore} from '@/ts/store/user-store';
import {storeToRefs} from 'pinia';
import {useFeatureAccess} from '@/ts/composables/stateful/use-feature-access';


/** Builds the default sidebar menu items (bookmarks, resources, FAQ, glossary, imprint) based on feature access */
export function useDefaultSideBarMenu(): BalmSidebarMenuItem[] {
  const {canAccessBookmarks} = useFeatureAccess();
  
  const baseItems: BalmSidebarMenuItem[] = [
    {
      type: BalmSidebarType.RESOURCES,
      icon: 'layout-grid-01.svg',
      labelKey: 'resources',
    },
    {
      type: BalmSidebarType.FAQ,
      icon: 'message-chat-square.svg',
      labelKey: 'faq',
    },
    {
      type: BalmSidebarType.GLOSSARY,
      icon: 'book-open.svg',
      labelKey: 'glossary',
    },
    {
      type: BalmSidebarType.IMPRINT,
      icon: 'file-shield.svg',
      labelKey: 'imprint',
    },
  ];

  if (canAccessBookmarks.value) {
    baseItems.unshift({
      type: BalmSidebarType.BOOKMARKS,
      icon: 'bookmark.svg',
      labelKey: 'bookmarks',
    });
  }

  return baseItems;
}

/** Provides a reactive sidebar menu that includes session recaps when the user has an active intervention */
export function useUserSidebarMenu(): { balmSidebarMenuItems: Ref<BalmSidebarMenuItem[]> } {
  const userStore = useUserStore();
  const {homeContent} = storeToRefs(userStore);

  const balmSidebarMenuItems = computed<BalmSidebarMenuItem[]>(() => {
    const homeScreenSideBar = useDefaultSideBarMenu();
    
    // Show Session Recaps if there's a current intervention (active or completed)
    // currentIntervention exists when user has joined at least one intervention
    return homeContent.value?.currentIntervention ? [
      {
        type: BalmSidebarType.INTERVENTION_RECAP,
        icon: 'list.svg',
        labelKey: 'session_recaps',
      },
      ...homeScreenSideBar,
    ] : homeScreenSideBar;
  });

  return {
    balmSidebarMenuItems,
  };
}

